<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
        <el-select v-model="tagGroup" style="width: 160px" @change="fetchList">
          <el-option label="全部" value="" />
          <el-option label="单身分类" value="class" />
          <el-option label="个性" value="personality" />
        </el-select>
        <el-button type="primary" @click="openCreate">新增标签</el-button>
      </div>
    </div>

    <p class="page-hint">
      字典仅管理员维护。用户资料 <code>users.tags</code> 用逗号保存名称；AI 梳理和个人朋友信息页都只能从启用项里勾选。
    </p>

    <el-table :data="items" v-loading="loading" style="width: 100%">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="name" label="名称" min-width="140" />
      <el-table-column prop="code" label="编码" min-width="140" />
      <el-table-column label="分组" width="110">
        <template #default="s">{{ groupLabel(s.row.tagGroup) }}</template>
      </el-table-column>
      <el-table-column prop="sortOrder" label="排序" width="80" />
      <el-table-column label="启用" width="80">
        <template #default="s">
          <el-tag :type="s.row.enabled ? 'success' : 'info'" size="small">
            {{ s.row.enabled ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="s">
          <el-button size="small" type="primary" @click="openEdit(s.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="remove(s.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="dialogVisible" :title="editForm.id ? '编辑标签' : '新增标签'" width="480px" destroy-on-close>
      <el-form label-width="96px">
        <el-form-item label="分组" required>
          <el-select v-model="editForm.tagGroup" style="width: 100%">
            <el-option label="单身分类" value="class" />
            <el-option label="个性" value="personality" />
          </el-select>
        </el-form-item>
        <el-form-item label="名称" required>
          <el-input v-model="editForm.name" maxlength="32" show-word-limit />
        </el-form-item>
        <el-form-item label="编码">
          <el-input v-model="editForm.code" placeholder="可空，自动生成" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="editForm.sortOrder" :min="0" :max="9999" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="editForm.enabled" :active-value="1" :inactive-value="0" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('jwt_token') || ''}`,
    'Content-Type': 'application/json'
  }
}

function emptyForm(tagGroup) {
  return {
    id: 0,
    name: '',
    code: '',
    tagGroup: tagGroup || 'class',
    sortOrder: 0,
    enabled: 1
  }
}

export default {
  name: 'AdminUserProfileTagManagement',
  data() {
    return {
      loading: false,
      saving: false,
      tagGroup: '',
      items: [],
      dialogVisible: false,
      editForm: emptyForm('class')
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    groupLabel(g) {
      return { class: '单身分类', personality: '个性' }[g] || g
    },
    async fetchList() {
      this.loading = true
      try {
        const response = await axios.post(
          '/api/v1/admin/profile/tag/list',
          { tagGroup: this.tagGroup || undefined, includeDisabled: 1 },
          { headers: authHeaders() }
        )
        if (response.data.code === 200) {
          this.items = response.data.data?.tags || []
        } else {
          ElMessage.error(response.data.message || '获取标签失败')
        }
      } catch {
        ElMessage.error('获取标签失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editForm = emptyForm(this.tagGroup || 'class')
      this.dialogVisible = true
    },
    openEdit(row) {
      this.editForm = {
        id: row.id,
        name: row.name,
        code: row.code,
        tagGroup: row.tagGroup,
        sortOrder: Number(row.sortOrder) || 0,
        enabled: Number(row.enabled) === 1 ? 1 : 0
      }
      this.dialogVisible = true
    },
    async save() {
      const name = (this.editForm.name || '').trim()
      if (!name) {
        ElMessage.warning('请填写名称')
        return
      }
      this.saving = true
      try {
        const url = this.editForm.id
          ? '/api/v1/admin/profile/tag/update'
          : '/api/v1/admin/profile/tag/add'
        const body = this.editForm.id
          ? {
              id: this.editForm.id,
              name,
              code: this.editForm.code,
              tagGroup: this.editForm.tagGroup,
              sortOrder: this.editForm.sortOrder,
              enabled: this.editForm.enabled
            }
          : {
              name,
              code: this.editForm.code,
              tagGroup: this.editForm.tagGroup,
              sortOrder: this.editForm.sortOrder,
              enabled: this.editForm.enabled
            }
        const response = await axios.post(url, body, { headers: authHeaders() })
        if (response.data.code === 200) {
          ElMessage.success('已保存')
          this.dialogVisible = false
          this.fetchList()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '保存失败')
      } finally {
        this.saving = false
      }
    },
    async remove(row) {
      try {
        await ElMessageBox.confirm(`删除标签「${row.name}」后用户不能再选它，确定吗？`, '删除确认', { type: 'warning' })
      } catch {
        return
      }
      try {
        const response = await axios.post(
          '/api/v1/admin/profile/tag/delete',
          { id: row.id },
          { headers: authHeaders() }
        )
        if (response.data.code === 200) {
          ElMessage.success('已删除')
          this.fetchList()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch {
        ElMessage.error('删除失败')
      }
    }
  }
}
</script>

<style scoped>
.page-hint {
  color: #606266;
  font-size: 13px;
  margin: 0 0 16px;
  line-height: 1.6;
}
</style>
